import {
  CloseOutlined,
  FilterFilled,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { useEffect, useState, useContext } from "react";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";

const { Text, Title } = Typography;

const layout = {
  labelCol: {
    span: 24,
    padding: 0,
  },
  wrapperCol: {
    span: 24,
  },
};

const tagRender = (props) => {
  const { label, value, closable, onClose, color } = props;
  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      color={color}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{
        marginRight: 3,
      }}
    >
      {label}
    </Tag>
  );
};

const maxTagPlaceHolder = (props) => {
  const { omittedValues, themeToken } = props;
  return (
    <Tooltip
      placement="bottom"
      title={omittedValues.map(({ label }) => (
        <p className="m-0">{label}</p>
      ))}
    >
      + {omittedValues.length}
    </Tooltip>
  );
};

const filterHeader = (props) => {
  const { activeKey, areas, seus, types, status, name, themeToken } = props;
  return (
    <div>
      <Flex justify="start" align="center" gap={"small"} wrap="wrap">
        <FilterFilled />
        <Text strong style={{ marginLeft: 4, marginRight: 12 }}>
          FILTER
        </Text>
        {activeKey?.length == 0 && types?.length > 0 ? (
          <Tag color="#536493" style={{ marginRight: 0, maxWidth: 200 }}>
            <Text ellipsis style={{ color: "white" }}>
              Device Types : + {types.length}
            </Text>
          </Tag>
        ) : (
          <></>
        )}
        {activeKey?.length == 0 && areas?.length > 0 ? (
          <Tag
            color={themeToken.purple6}
            style={{ marginRight: 0, maxWidth: 100 }}
          >
            <Text ellipsis style={{ color: "white" }}>
              Areas : + {areas.length}
            </Text>
          </Tag>
        ) : (
          <></>
        )}
        {activeKey?.length == 0 && seus?.length > 0 ? (
          <Tag
            color={themeToken.cyan6}
            style={{ marginRight: 0, maxWidth: 100 }}
          >
            <Text ellipsis style={{ color: "white" }}>
              SEUs : + {seus.length}
            </Text>
          </Tag>
        ) : (
          <></>
        )}
        {activeKey?.length == 0 && status?.length > 0 ? (
          status?.map((s) => {
            return (
              <Tag
                color={
                  s == "Online"
                    ? themeToken.colorSuccess
                    : s == "Warning"
                    ? themeToken.colorWarning
                    : s == "Error"
                    ? themeToken.colorError
                    : s == "Offline"
                    ? "grey"
                    : themeToken.colorPrimary
                }
                style={{ marginRight: 0, maxWidth: 100 }}
              >
                <Text style={{ color: "white" }}>{s}</Text>
              </Tag>
            );
          })
        ) : (
          <></>
        )}
        {activeKey?.length == 0 && name?.length > 0 ? (
          <Tag
            color={themeToken.colorPrimary}
            style={{ marginRight: 0, maxWidth: 100 }}
          >
            <Text ellipsis={{ tooltip: name }} style={{ color: "white" }}>
              Name : {name}
            </Text>
          </Tag>
        ) : (
          <></>
        )}
      </Flex>
    </div>
  );
};

const { Option } = Select;

function DeviceFilter({
  themeToken,
  activeKey,
  setActiveKey,
  filterValues,
  setFilterValues,
}) {
  const [form] = Form.useForm();

  const { messageApi, siteSelected } = useContext(IndexContext);
  const [fetchWrapper] = useFetch();

  const [loading, setLoading] = useState(false);

  // area selection
  const [areaList, setAreaList] = useState([]);
  const [areaSelected, setAreaSelected] = useState(null);

  // seu selection
  const [seuList, setSeuList] = useState([]);
  const [seuSelected, setSeuSelected] = useState(null);

  // device status selection
  const [deviceStatusList, setDeviceStatusList] = useState([]);
  const [deviceStatusSelected, setDeviceStatusSelected] = useState(null);

  // device type selection
  const [deviceTypeList, setDeviceTypeList] = useState([]);
  const [deviceTypeSelected, setDeviceTypeSelected] = useState(null);

  /* API to get area selection */
  function getAreaList() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "site_management/getAreaList",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setAreaList(fetch_output.area_list);
        }
        setLoading(false);
      },
    });
  }

  /* API to get seu selection */
  function getSeuList() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "site_management/getSeuList",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setSeuList(fetch_output.seu_list);
        }
        setLoading(false);
      },
    });
  }

  /* API to get device status selection */
  function getDeviceStatusList() {
    setLoading(true);
    fetchWrapper({
      method: "GET",
      endpoint_url: "device_monitoring/getDeviceStatusList",
      onSuccess: (response) => {
        setDeviceStatusList(response.data);
        setLoading(false);
      },
    });
  }

  /* API to get device type selection */
  function getDeviceTypeList() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "site_management/getMeterType",
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceTypeList(fetch_output.meter_type_list);
        }
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getAreaList();
      getSeuList();
      getDeviceStatusList();
      getDeviceTypeList();
    }
  }, [siteSelected]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Collapse: {
            algorithm: true,
            headerBg: "linear-gradient(#e66465, #9198e5)",
          },
        },
      }}
    >
      <Collapse
        size="middle"
        expandIconPosition="end"
        defaultActiveKey={activeKey}
        onChange={(key) => setActiveKey(key)}
      >
        <Collapse.Panel
          header={filterHeader({
            activeKey,
            ...form.getFieldsValue(true),
            themeToken,
          })}
          key={1}
        >
          <Form
            {...layout}
            form={form}
            onFinish={setFilterValues}
            clearOnDestroy={false}
            autoComplete="off"
          >
            <Row gutter={[12, 0]}>
              <Col xs={24} sm={24} md={24} lg={12} xl={5} xxl={5}>
                <Form.Item name="types" label={<Text strong>Device Type</Text>}>
                  <Select
                    placeholder={
                      <Tag color={"#536493"} className="pl-3 pr-3">
                        All
                      </Tag>
                    }
                    allowClear
                    tagRender={(props) =>
                      tagRender({ color: "#536493", ...props })
                    }
                    showSearch={true}
                    optionFilterProp="label"
                    mode="multiple"
                    maxTagCount="responsive"
                    maxTagPlaceholder={(omittedValues) =>
                      maxTagPlaceHolder({
                        omittedValues: omittedValues,
                        themeToken,
                      })
                    }
                    value={deviceTypeSelected}
                    options={deviceTypeList}
                    onChange={(value) => {
                      setDeviceTypeSelected(value);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={5} xxl={5}>
                <Form.Item name="areas" label={<Text strong>Area</Text>}>
                  <Select
                    allowClear
                    tagRender={(props) =>
                      tagRender({ color: themeToken.purple6, ...props })
                    }
                    showSearch={true}
                    optionFilterProp="label"
                    mode="multiple"
                    placeholder={
                      <Tag color={themeToken.purple6} className="pl-3 pr-3">
                        All
                      </Tag>
                    }
                    maxTagCount="responsive"
                    maxTagPlaceholder={(omittedValues) =>
                      maxTagPlaceHolder({
                        omittedValues: omittedValues,
                        themeToken,
                      })
                    }
                    value={areaSelected}
                    options={areaList}
                    onChange={(value) => {
                      setAreaSelected(value);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={5} xxl={5}>
                <Form.Item name="seus" label={<Text strong>SEU</Text>}>
                  <Select
                    placeholder={
                      <Tag color={themeToken.cyan6} className="pl-3 pr-3">
                        All
                      </Tag>
                    }
                    allowClear
                    tagRender={(props) =>
                      tagRender({ color: themeToken.cyan6, ...props })
                    }
                    showSearch={true}
                    optionFilterProp="label"
                    mode="multiple"
                    maxTagCount="responsive"
                    maxTagPlaceholder={(omittedValues) =>
                      maxTagPlaceHolder({
                        omittedValues: omittedValues,
                        themeToken,
                      })
                    }
                    value={seuSelected}
                    options={seuList}
                    onChange={(value) => {
                      setSeuSelected(value);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={4} xxl={4}>
                <Form.Item name="status" label={<Text strong>Status</Text>}>
                  <Select
                    placeholder={
                      <Tag
                        color={themeToken.colorPrimary}
                        className="pl-3 pr-3"
                      >
                        All
                      </Tag>
                    }
                    allowClear
                    tagRender={(props) =>
                      tagRender({
                        color:
                          props.value == "Online"
                            ? themeToken.colorSuccess
                            : props.value == "Warning"
                            ? themeToken.colorWarning
                            : props.value == "Error"
                            ? themeToken.colorError
                            : props.value == "Offline"
                            ? "grey"
                            : themeToken.colorPrimary,
                        ...props,
                      })
                    }
                    showSearch={false}
                    mode="multiple"
                    maxTagCount="responsive"
                    maxTagPlaceholder={(omittedValues) =>
                      maxTagPlaceHolder({
                        omittedValues: omittedValues,
                        themeToken,
                      })
                    }
                    value={deviceStatusSelected}
                    options={deviceStatusList}
                    onChange={(value) => {
                      setDeviceStatusSelected(value);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={5} xxl={5}>
                <Form.Item name="name" label={<Text strong>Name</Text>}>
                  <Input allowClear placeholder="Search Name"></Input>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Flex justify="end" align="center" gap="middle">
                  <Button
                    type="default"
                    icon={<CloseOutlined />}
                    onClick={() => form.resetFields()}
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Form>
        </Collapse.Panel>
      </Collapse>
    </ConfigProvider>
  );
}

export default DeviceFilter;
